import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowDown, Menu, X,
  BookOpen, TrendingUp, Lightbulb, Shield, GraduationCap,
  Users, Building2, Heart, CheckCircle, BarChart3, Target,
  Lock, Globe, UserCheck, Trophy
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { QHubBuddyLogo, QHubIcon } from "@/components/QHubBranding";
import { NeuralBackground } from "@/components/NeuralBackground";
import VersionBadge from "@/components/VersionBadge";
import BuddyAvanzaLegalFooter from "@/components/BuddyAvanzaLegalFooter";
import { translations } from "@/i18n/landing";

/* ── images ── */
const IMG = {
  hero: "https://images.pexels.com/photos/5306449/pexels-photo-5306449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const GRADE_COLORS = [
  "#419cca", // 4°
  "#4b8ac8", // 5°
  "#5678c6", // 6°
  "#6366f1", // 7°
  "#7c5eea", // 8°
  "#8b5cf6", // 9°
  "#c94ce0", // 1° Bach
  "#f59e0b", // 2° Bach
];

/* Actor icons map (stable) */
const ACTOR_ICONS = [Building2, BookOpen, GraduationCap, Heart];
const ACTOR_COLORS = ["#419cca", "#8b5cf6", "#f59e0b", "#10b981"];
const DIFF_ICONS = [UserCheck, Lock, Shield, Globe];
const DIFF_COLORS = ["#419cca", "#8b5cf6", "#10b981", "#f59e0b"];
const VIS_ICONS = [GraduationCap, TrendingUp, Target];
const VIS_COLORS = ["#419cca", "#8b5cf6", "#f59e0b"];
const PILLAR_ICONS = [Target, BarChart3, Lightbulb, Trophy];
const PILLAR_COLORS = ["#419cca", "#8b5cf6", "#f59e0b", "#10b981"];

/* ── parallax image ── */
const ParallaxImg = ({ src, alt, className = "", speed = 0.15, testId }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${speed * 100}%`, `${speed * 100}%`]);
  return (
    <div ref={ref} className={`overflow-hidden rounded-3xl ${className}`}>
      <motion.img src={src} alt={alt} style={{ y }} className="w-full h-[130%] object-cover" data-testid={testId} />
    </div>
  );
};

/* ── animations ── */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 50, filter: "blur(8px)" }}
    whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >{children}</motion.div>
);

const StaggerContainer = ({ children, className = "" }) => (
  <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={{ visible: { transition: { staggerChildren: 0.12 } } }} className={className}>
    {children}
  </motion.div>
);

const StaggerItem = ({ children, className = "" }) => (
  <motion.div variants={{ hidden: { opacity: 0, y: 40, scale: 0.95 }, visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } }} className={className}>
    {children}
  </motion.div>
);

const Login = () => {
  const { lang } = useParams();
  const t = translations[lang] || translations.es;
  const otherLang = lang === "en" ? "es" : "en";
  const otherLabel = lang === "en" ? "ES" : "EN";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgScale = useTransform(heroScroll, [0, 1], [1, 1.15]);
  const heroImgY = useTransform(heroScroll, [0, 1], ["0%", "15%"]);

  return (
    <div className="min-h-screen flex flex-col bg-white font-['Outfit']" style={{ scrollBehavior: "smooth" }} data-testid="landing-page">

      {/* ═══ HEADER ═══ */}
      <motion.header initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="fixed top-0 left-0 right-0 z-50 bg-white/60 backdrop-blur-xl border-b border-gray-100/60" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <QHubBuddyLogo size="default" />
            <nav className="hidden md:flex items-center gap-6" data-testid="landing-nav">
              <Link to="/buddy-avanza" className="text-sm font-semibold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:from-[#3a8ab5] hover:to-[#7a4ee0] px-4 py-2 rounded-full transition-all hover:shadow-lg hover:shadow-[#8b5cf6]/30 hover:scale-105 active:scale-95" data-testid="nav-buddy-avanza-cta">
                BUDDY AVANZA — Comprar acceso
              </Link>
              <a href="#modelo" className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-testid="nav-modelo-link">{t.nav.model}</a>
              <a href="#impacto" className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-testid="nav-componentes-link">{t.nav.components}</a>
              <a href="#neurociencia" className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-testid="nav-neurociencia-link">{t.nav.neuroscience}</a>
              <Link to={`/${otherLang}`} className="text-xs font-bold text-gray-400 hover:text-gray-900 border border-gray-200 rounded-full px-3 py-1 transition-colors" data-testid="lang-switch">
                {otherLabel}
              </Link>
              <Link to="/login" className="text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 px-5 py-2 rounded-full transition-all hover:shadow-lg hover:shadow-gray-900/20 hover:scale-105 active:scale-95" data-testid="nav-login-link">
                {t.nav.login}
              </Link>
            </nav>
            <button className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-gray-100"
              data-testid="mobile-menu"
            >
              <nav className="flex flex-col px-6 py-4 gap-3">
                <Link to="/buddy-avanza" onClick={() => setMobileMenuOpen(false)} className="text-center text-sm font-semibold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] px-5 py-2.5 rounded-full" data-testid="mobile-buddy-avanza-cta">
                  BUDDY AVANZA — Comprar acceso
                </Link>
                <a href="#modelo" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-gray-600 hover:text-gray-900 py-2">{t.nav.model}</a>
                <a href="#impacto" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-gray-600 hover:text-gray-900 py-2">{t.nav.components}</a>
                <a href="#neurociencia" onClick={() => setMobileMenuOpen(false)} className="text-base font-medium text-gray-600 hover:text-gray-900 py-2">{t.nav.neuroscience}</a>
                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  <Link to={`/${otherLang}`} onClick={() => setMobileMenuOpen(false)} className="text-sm font-bold text-gray-400 hover:text-gray-900 border border-gray-200 rounded-full px-4 py-2" data-testid="mobile-lang-switch">{otherLabel}</Link>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1 text-center text-sm font-semibold text-white bg-gray-900 px-5 py-2.5 rounded-full" data-testid="mobile-login-link">{t.nav.login}</Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <NeuralBackground />

      {/* ═══════════════════════════════════════
          1. HERO
      ═══════════════════════════════════════ */}
      <section ref={heroRef} className="relative pt-28 pb-0 px-4 overflow-hidden" data-testid="hero-section">
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-center pt-12 pb-16 max-w-3xl mx-auto">

            <motion.span
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="inline-block px-4 py-1.5 rounded-full border border-gray-200 text-gray-500 text-xs font-medium tracking-widest uppercase mb-8"
              data-testid="hero-label"
            >
              {t.hero.label}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.05] mb-8 tracking-tight"
              data-testid="hero-title"
            >
              {t.hero.title_1}<br className="hidden sm:block" /> {t.hero.title_2}
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="flex justify-center mb-8"
            >
              <QHubBuddyLogo size="large" />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="text-lg sm:text-xl text-gray-500 leading-relaxed mb-10 max-w-xl mx-auto font-light"
              data-testid="hero-description"
            >
              {t.hero.subtitle_1_a}<br />
              {t.hero.subtitle_1_b}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex justify-center"
            >
              <Link to="/contacto" className="inline-block" data-testid="hero-cta-experience-wrapper">
                <Button className="h-12 px-10 rounded-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white font-semibold text-base hover:from-[#3a8ab5] hover:to-[#7a4ee0] transition-all hover:shadow-xl hover:shadow-[#8b5cf6]/30 hover:scale-105 active:scale-95"
                        data-testid="hero-cta-experience">
                  Vive la experiencia <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-5xl"
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-gray-400/30 relative">
              <motion.img src={IMG.hero} alt={t.hero.subtitle_1_a} style={{ scale: heroImgScale, y: heroImgY }} className="w-full h-[320px] sm:h-[420px] lg:h-[480px] object-cover origin-center" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }} className="flex justify-center pt-10 pb-6">
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
              <ArrowDown className="w-5 h-5 text-gray-300" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          1.5. BUDDY AVANZA bloque comercial — RETIRADO del Home global
          (mantiene su promoción en /buddy-avanza y en el header CTA).
      ═══════════════════════════════════════ */}

      {/* ═══════════════════════════════════════
          2. DECLARACIÓN — ¿Qué es QHUB.BUDDY?
      ═══════════════════════════════════════ */}
      <section id="modelo" className="py-28 px-4" data-testid="section-declaration">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <span className="inline-block text-xs font-semibold tracking-widest text-[#419cca] uppercase mb-6 px-4 py-1.5 rounded-full border border-[#419cca]/20 bg-[#419cca]/5">{t.declaration.label}</span>
            <p className="text-2xl sm:text-3xl font-light text-gray-700 leading-relaxed max-w-2xl mx-auto mb-5">
              {t.declaration.text_pre}{" "}
              <span className="font-semibold text-gray-900">{t.declaration.text_bold}</span>
            </p>
            <p className="text-base text-gray-400 max-w-xl mx-auto font-light">
              {t.declaration.sub}
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-20">
              {t.declaration.pillars.map((p, i) => {
                const Icon = PILLAR_ICONS[i];
                const color = PILLAR_COLORS[i];
                return (
                  <motion.div key={p.label || i} whileHover={{ y: -6 }} transition={{ duration: 0.25 }} className="flex flex-col items-center gap-4 py-8 px-6 rounded-2xl border border-gray-100 bg-white" data-testid={`pillar-${i}`}>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                      <Icon className="w-6 h-6" style={{ color }} />
                    </div>
                    <p className="text-lg font-bold text-gray-900">{p.label}</p>
                    <p className="text-sm text-gray-500 text-center leading-relaxed">{p.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          3. TRANSFORMACIÓN DEL APRENDIZAJE
      ═══════════════════════════════════════ */}
      <section className="py-28 px-4 bg-gray-50/60" data-testid="section-transformation">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block text-xs font-semibold tracking-widest text-[#419cca] uppercase mb-6 px-4 py-1.5 rounded-full border border-[#419cca]/20 bg-[#419cca]/5">{t.transformation.label}</span>
              {t.transformation.title && <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">{t.transformation.title}</h2>}
            </div>
          </Reveal>
          <StaggerContainer className="space-y-0">
            {t.transformation.items.map((item, i) => (
              <StaggerItem key={item.title || item.n || i}>
                <motion.div
                  whileHover={{ x: 8, backgroundColor: "rgba(65,156,202,0.02)" }}
                  transition={{ duration: 0.3 }}
                  className="flex items-start gap-8 py-8 border-b border-gray-100 px-4 -mx-4 rounded-xl cursor-default"
                  data-testid={`transformation-item-${i}`}
                >
                  <span className="text-3xl font-bold text-gray-100 shrink-0 w-12 text-right select-none">{item.n}</span>
                  <div>
                    <p className="text-xl font-semibold text-gray-900 mb-1">{item.title}</p>
                    <p className="text-base text-gray-500 font-light leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          4. CÓMO FUNCIONA
      ═══════════════════════════════════════ */}
      <section id="impacto" className="py-28 px-4" data-testid="section-how">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block text-xs font-semibold tracking-widest text-[#419cca] uppercase mb-6 px-4 py-1.5 rounded-full border border-[#419cca]/20 bg-[#419cca]/5">{t.how.label}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">{t.how.title}</h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.how.steps.map((step, i) => {
                const stepColors = ["#419cca", "#6366f1", "#8b5cf6", "#f59e0b"];
                const color = stepColors[i];
                return (
                  <motion.div
                    key={step.title || step.n || i}
                    whileHover={{ y: -8, boxShadow: "0 20px 40px -12px rgba(0,0,0,0.1)" }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl p-7 border border-gray-100 flex flex-col gap-4 cursor-default"
                    data-testid={`step-${i}`}
                  >
                    <span className="text-4xl font-bold" style={{ color: `${color}30` }}>{step.n}</span>
                    <div>
                      <p className="text-base font-bold text-gray-900 mb-2">{step.title}</p>
                      <p className="text-sm text-gray-500 leading-relaxed font-light">{step.desc}</p>
                    </div>
                    <div className="mt-auto h-1 w-8 rounded-full" style={{ backgroundColor: color }} />
                  </motion.div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          5. VISIBILIDAD / RESULTADOS
      ═══════════════════════════════════════ */}
      <section className="py-28 px-4 bg-gray-900" data-testid="section-visibility">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block text-xs font-semibold tracking-widest text-[#419cca] uppercase mb-6 px-4 py-1.5 rounded-full border border-[#419cca]/20 bg-[#419cca]/5">{t.visibility.label}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">{t.visibility.title}</h2>
              <p className="text-lg text-gray-400 font-light">{t.visibility.subtitle}</p>
            </div>
          </Reveal>
          <StaggerContainer className="grid sm:grid-cols-3 gap-6 mb-16">
            {t.visibility.items.map((item, i) => {
              const Icon = VIS_ICONS[i];
              const color = VIS_COLORS[i];
              return (
                <StaggerItem key={item.title || i}>
                  <div className="rounded-2xl p-7 border border-white/5 bg-white/5 backdrop-blur-sm" data-testid={`visibility-item-${i}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${color}20` }}>
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <p className="text-base font-semibold text-white mb-2">{item.title}</p>
                    <p className="text-sm text-gray-400 leading-relaxed font-light">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
          <Reveal delay={0.3}>
            <p className="text-center text-lg sm:text-xl text-gray-300 font-light italic max-w-xl mx-auto border-t border-white/10 pt-12">
              {t.visibility.quote}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          6. IMPACTO POR ACTOR
      ═══════════════════════════════════════ */}
      <section className="py-28 px-4" data-testid="section-actors">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block text-xs font-semibold tracking-widest text-[#419cca] uppercase mb-6 px-4 py-1.5 rounded-full border border-[#419cca]/20 bg-[#419cca]/5">{t.actors.label}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">{t.actors.title}</h2>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 gap-6">
              {t.actors.list.map((actor, i) => {
                const Icon = ACTOR_ICONS[i];
                const color = ACTOR_COLORS[i];
                return (
                  <motion.div
                    key={actor.role || i}
                    whileHover={{ y: -6, boxShadow: "0 20px 40px -12px rgba(0,0,0,0.08)" }}
                    transition={{ duration: 0.3 }}
                    className="bg-white rounded-2xl p-8 border border-gray-100 flex gap-5 cursor-default"
                    data-testid={`actor-card-${i}`}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}12` }}>
                      <Icon className="w-6 h-6" style={{ color }} />
                    </div>
                    <div>
                      <p className="text-base font-bold text-gray-900 mb-3">{actor.role}</p>
                      {actor.lines.map((line, j) => (
                        <p key={`${actor.role || j}-${line.slice(0, 24)}`} className="text-sm text-gray-500 leading-relaxed font-light mb-1">{line}</p>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          7. FUNDAMENTO PEDAGÓGICO
      ═══════════════════════════════════════ */}
      <section id="neurociencia" className="py-28 px-4" data-testid="section-science">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="flex justify-center mb-6"><QHubBuddyLogo size="large" /></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4 tracking-tight" data-testid="science-title">{t.science.title}</h2>
            <p className="text-base sm:text-lg text-gray-400 text-center mb-20 font-light max-w-2xl mx-auto" data-testid="science-subtitle">{t.science.subtitle}</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24">
            <Reveal delay={0.1}>
              <div data-testid="science-col-theory">
                <h3 className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-8">{t.science.theory_title}</h3>
                <div className="space-y-5">
                  {t.science.theories.map((item, i) => (
                    <motion.div key={item.author || i} whileHover={{ x: 6 }} transition={{ duration: 0.2 }} className="flex items-baseline gap-4 cursor-default" data-testid={`theory-item-${i}`}>
                      <span className="text-base font-bold text-gray-900 whitespace-nowrap">{item.author}</span>
                      <span className="text-gray-300">—</span>
                      <span className="text-sm text-gray-500">{item.concept}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div data-testid="science-col-principles">
                <h3 className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-8">{t.science.principles_title}</h3>
                <div className="space-y-4">
                  {t.science.principles.map((item, i) => (
                    <motion.div key={typeof item === "string" ? item.slice(0, 32) : i} whileHover={{ x: 6 }} transition={{ duration: 0.2 }} className="flex items-center gap-3 cursor-default" data-testid={`principle-item-${i}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-[#419cca] shrink-0" />
                      <span className="text-sm text-gray-600">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          8. NIVELES EDUCATIVOS
      ═══════════════════════════════════════ */}
      <section className="py-28 px-4" data-testid="section-levels">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4 tracking-tight" data-testid="levels-title">{t.levels.title}</h2>
            <p className="text-base sm:text-lg text-gray-400 text-center mb-16 font-light" data-testid="levels-subtitle">{t.levels.subtitle}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="hidden sm:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#419cca] via-[#8b5cf6] to-[#f59e0b] rounded-full -translate-y-1/2 opacity-20" />
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 sm:gap-6">
                {t.levels.grades.map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 30, scale: 0.9 }} whileInView={{ opacity: 1, y: 0, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} whileHover={{ y: -8, scale: 1.05 }} className="relative flex flex-col items-center cursor-default" data-testid={`level-${i}`}>
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center mb-3 shadow-lg" style={{ backgroundColor: `${GRADE_COLORS[i]}12`, borderColor: `${GRADE_COLORS[i]}30`, borderWidth: 2 }}>
                      <span className="text-2xl sm:text-3xl font-bold" style={{ color: GRADE_COLORS[i] }}>{item.grade}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  </motion.div>
                ))}
              </div>
              <Reveal delay={0.5}>
                <div className="flex justify-center mt-12">
                  <div className="bg-gradient-to-r from-[#419cca] to-[#f59e0b] rounded-full px-8 py-3 shadow-lg shadow-[#419cca]/10">
                    <p className="text-white text-sm font-semibold tracking-wide text-center">{t.levels.badge}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          9. DIFERENCIAL
      ═══════════════════════════════════════ */}
      <section className="py-28 px-4 bg-gray-50/80" data-testid="section-differential">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight leading-tight" data-testid="differential-title">
              {t.differential.title_1}<br />
              <span className="text-[#419cca]">{t.differential.title_2}</span>
            </p>
            <p className="text-lg text-gray-400 font-light mb-14 max-w-lg mx-auto">{t.differential.subtitle}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 justify-items-center">
              {t.differential.items.map((item, i) => {
                const Icon = DIFF_ICONS[i];
                const color = DIFF_COLORS[i];
                return (
                  <div key={typeof item === "string" ? item.slice(0, 32) : i} className="flex items-center gap-3 bg-white rounded-2xl px-5 py-4 border border-gray-100 shadow-sm w-full justify-center" data-testid={`differential-item-${i}`}>
                    <Icon className="w-4 h-4 shrink-0" style={{ color }} />
                    <span className="text-sm font-medium text-gray-700 text-center">{item}</span>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          10. CTA FINAL
      ═══════════════════════════════════════ */}
      <section className="py-32 px-4 bg-gray-900 relative overflow-hidden" data-testid="section-final">
        <div className="absolute inset-0 bg-gradient-to-br from-[#419cca]/10 to-[#8b5cf6]/10" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <Reveal>
            <div className="flex justify-center mb-8">
              <QHubIcon size="large" />
            </div>
            <p className="text-xs font-bold tracking-widest uppercase text-[#419cca] mb-4" data-testid="final-label">{t.finalcta.label}</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight leading-tight" data-testid="final-title">
              {t.finalcta.title_1}<br className="hidden sm:block" /> {t.finalcta.title_2}
            </h2>
            <p className="text-lg text-gray-400 font-light mb-12 max-w-md mx-auto">
              {t.finalcta.subtitle}
            </p>
            <Link to="/contacto" className="inline-block" data-testid="final-cta-experience-wrapper">
              <Button className="h-13 px-12 rounded-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white font-semibold text-base hover:from-[#3a8ab5] hover:to-[#7a4ee0] transition-all hover:shadow-2xl hover:shadow-[#8b5cf6]/40 hover:scale-105 active:scale-95"
                      data-testid="final-cta-experience">
                Vive la experiencia <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-10 px-4 bg-gray-900 border-t border-gray-800" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <QHubIcon size="small" />
              <div>
                <p className="text-lg font-bold text-white" data-testid="footer-brand">QHUB.BUDDY</p>
                <p className="text-gray-400 text-sm" data-testid="footer-slogan">{t.footer.slogan}</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm" data-testid="footer-tagline">{t.footer.tagline}</p>
            <div className="flex items-center gap-4">
              <a href="https://www.qhubglobal.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-white transition-colors" data-testid="footer-qhub-link">qhubglobal.com</a>
              <VersionBadge className="text-gray-500" />
            </div>
          </div>
        </div>
      </footer>
      <BuddyAvanzaLegalFooter />
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════
   FASE N1CO-0 — Bloque BUDDY AVANZA (Home público)
   Consume /api/avanzo-b2c/offer-config. CTA → /avanzo/comprar
   (NUNCA directo a N1CO).
   ═════════════════════════════════════════════════════════════ */
const BuddyAvanzaBlock = () => {
  const [offer, setOffer] = React.useState(null);
  React.useEffect(() => {
    let cancel = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avanzo-b2c/offer-config`)
      .then((r) => r.json())
      .then((d) => { if (!cancel) setOffer(d); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);
  if (!offer) return null;
  const price = Number(offer.offer_price).toFixed(2);
  const dailyDisplay = offer.daily_equivalent_display;
  return (
    <section
      id="buddy-avanza"
      data-testid="buddy-avanza-block"
      className="relative py-20 px-4 overflow-hidden bg-gradient-to-b from-white via-[#eef4fb] to-white"
    >
      {/* soft ambient dots */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 left-10 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: "#419cca" }} />
        <div className="absolute bottom-0 right-10 w-80 h-80 rounded-full blur-3xl opacity-25" style={{ background: "#8b5cf6" }} />
      </div>
      <div className="relative max-w-6xl mx-auto">
        <div className="rounded-[28px] border border-white/60 bg-white/80 backdrop-blur-xl p-8 md:p-14 shadow-[0_30px_80px_-30px_rgba(65,156,202,0.35)] ring-1 ring-slate-900/[0.02]">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <div className="flex items-center gap-4 mb-4">
                <img src="/buddy-avanza-logo-transparent.png" alt="BUDDY AVANZA"
                      className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-[0_10px_25px_rgba(139,92,246,0.25)]"
                      data-testid="home-block-logo" />
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-widest text-[#8b5cf6] uppercase px-3 py-1 rounded-full border border-[#8b5cf6]/25 bg-white shadow-sm"
                       data-testid="buddy-avanza-brand-label">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8b5cf6] animate-pulse" />
                  {offer.brand_name}
                </span>
              </div>
              <h2 className="mt-4 text-3xl sm:text-5xl font-bold text-gray-900 tracking-tight leading-[1.05]"
                   data-testid="buddy-avanza-title">
                Tu preparación para
                <span className="block bg-gradient-to-r from-[#419cca] via-[#6d5cf5] to-[#8b5cf6] bg-clip-text text-transparent">
                  AVANZO empieza aquí.
                </span>
              </h2>
              <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl"
                  data-testid="buddy-avanza-description">
                Practica las 4 áreas, descubre qué necesitas reforzar y recibe
                acompañamiento personalizado de Buddy — paso a paso.
              </p>
              <p className="mt-3 text-base font-semibold text-[#0f172a]"
                  data-testid="buddy-avanza-tagline">
                {offer.brand_tagline}
              </p>
              <ul className="mt-5 grid grid-cols-2 gap-2 text-sm text-gray-700"
                   data-testid="buddy-avanza-subjects">
                {(offer.subjects || []).map((s, i) => (
                  <li key={s} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{
                      background: ["#419cca","#8b5cf6","#10b981","#f59e0b"][i % 4],
                    }} />
                    {s}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/buddy-avanza" data-testid="buddy-avanza-cta"
                       className="group inline-flex items-center justify-center px-6 py-3.5 rounded-full text-base font-semibold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:brightness-110 shadow-lg shadow-[#8b5cf6]/25 transition-all hover:scale-[1.02] active:scale-95">
                  Comenzar mi preparación
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/buddy-avanza#como-funciona" data-testid="buddy-avanza-cta-secondary"
                       className="inline-flex items-center justify-center px-6 py-3.5 rounded-full text-sm font-semibold text-slate-800 border border-slate-200 bg-white hover:bg-slate-50 transition-all">
                  Ver cómo funciona
                </Link>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="relative rounded-2xl bg-white border border-slate-200 p-7 shadow-2xl shadow-[#8b5cf6]/10 ring-1 ring-slate-900/[0.02]">
                <div className="absolute -top-3 left-6 inline-flex items-center gap-2 text-[10px] tracking-[2px] uppercase font-semibold px-3 py-1 rounded-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white shadow-lg">
                  Oferta especial
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-black tracking-tight text-slate-900"
                         data-testid="buddy-avanza-price">
                    US${price}
                  </span>
                  <span className="text-slate-500 text-sm">USD</span>
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-700"
                      data-testid="buddy-avanza-modality">
                  Pago único · 2 meses de acceso
                </div>
                <div className="mt-4 space-y-1 text-xs">
                  {dailyDisplay && (
                    <div className="text-emerald-600 font-semibold" data-testid="buddy-avanza-daily">
                      {dailyDisplay}
                    </div>
                  )}
                  <div className="text-slate-500" data-testid="buddy-avanza-no-renew">
                    Sin renovación automática
                  </div>
                </div>
                <Link
                  to="/buddy-avanza"
                  className="mt-6 inline-flex items-center justify-center w-full text-sm font-semibold text-white bg-gradient-to-r from-[#419cca] to-[#8b5cf6] hover:brightness-110 px-6 py-3 rounded-full transition-all hover:scale-[1.02] active:scale-95 shadow-lg shadow-[#8b5cf6]/25"
                >
                  Ver detalles y comprar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <p className="mt-3 text-[10px] text-center text-slate-400">
                  Pago seguro procesado por N1CO
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
